import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Container,
  Grid,
  Link,
  Typography,
  List,
  ListItem,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { theme } from '@pagopa/mui-italia';
import { useTranslation } from 'react-i18next';
import { Link as LinkRouter } from 'react-router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SEO from '../SEO/Seo';
import ReactMarkdown from 'react-markdown';
import { links } from '../../links';

type FaqItem = {
  category: string;
  question: string;
  answer: string;
};

const FaqAnswerContent = ({ content }: { content: string }) => {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => (
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {children}
          </Typography>
        ),
        ul: ({ children }) => (
          <List
            sx={{
              listStyleType: 'disc',
              pl: 2,
              mb: 1.5,
              '& .MuiListItem-root': {
                display: 'list-item',
                py: 0.5
              }
            }}
          >
            {children}
          </List>
        ),
        li: ({ children }) => (
          <ListItem
            sx={{
              display: 'list-item',
              p: 0,
              mb: 0.5,
              '&::marker': {
                color: theme.palette.primary.main
              }
            }}
          >
            <ListItemText primary={children} disableTypography />
          </ListItem>
        ),
        strong: ({ children }) => (
          <Typography component="span" sx={{ fontWeight: '600' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        a: ({ href, children }) => (
          <Link href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </Link>
        )
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default function FaqPage() {
  const { t } = useTranslation();
  const { t: tFaq } = useTranslation('faq');
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  // Use FAQ lang as data source
  const faqList = tFaq('faqList', { returnObjects: true }) as Array<FaqItem>;

  // Group FAQs by category
  const groupedFaqs = faqList.reduce(
    (acc: Record<string, Array<FaqItem>>, item: FaqItem) => {
      const category = item.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    },
    {}
  );

  return (
    <>
      <SEO title={t('faqPage.title')} description={t('faqPage.desc')} />
      <Box bgcolor={'background.paper'} px={{ xs: 2, md: 0 }}>
        <Container maxWidth={'md'}>
          <Box>
            <Breadcrumbs aria-label="breadcrumbs" sx={{ py: 2 }} separator="›">
              <Link
                component={LinkRouter}
                display="flex"
                alignItems="center"
                color={smUp ? 'textSecondary' : 'textPrimary'}
                fontWeight={smUp ? '400' : '600'}
                to={links.public}
                underline="none"
              >
                {!smUp && <ArrowBackIcon sx={{ mr: 1 }} />}
                Home
              </Link>
              {smUp && (
                <Typography
                  color="textPrimary"
                  fontWeight={'600'}
                  aria-current="page"
                >
                  {t('faqPage.title')}
                </Typography>
              )}
            </Breadcrumbs>
            <Box py={6}>
              <Typography variant="h2" component="h1">
                {t('faqPage.title')}
              </Typography>
              <Typography variant="body2" component="p" my={2}>
                {t('faqPage.desc')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      <Box id="faqPageList" bgcolor={theme.palette.grey[50]} py={6} px={2}>
        <Container maxWidth={'md'}>
          {Object.keys(groupedFaqs).map((category, cindex) => (
            <Grid container key={cindex} my={{ xs: 2, md: 4 }}>
              <Grid item xs={12} md={5}>
                <Typography variant="h4" component="h2">
                  {category}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                {groupedFaqs[category].map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      my: 1,
                      // removes default accordion separator
                      '&:before': {
                        display: 'none'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${cindex}-${index}-content`}
                      id={`panel${cindex}-${index}-header`}
                      sx={{ fontWeight: '600' }}
                    >
                      {faq.question}
                    </AccordionSummary>
                    <AccordionDetails>
                      <FaqAnswerContent content={faq.answer} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            </Grid>
          ))}
        </Container>
      </Box>
    </>
  );
}
